import React, { useState, useRef, useEffect, useCallback } from "react";
import { useAuth } from "@/App";
import { useLanguage } from "@/i18n/LanguageContext";
import { useTheme } from "@/theme/ThemeContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import axios from "axios";
import { toast } from "sonner";
import {
  ClipboardCheck, Upload, FileText, Image as ImageIcon, Loader2, X,
  CheckCircle2, AlertTriangle, AlertCircle, Info, Sparkles, ListChecks, Copy,
  History as HistoryIcon, ChevronRight,
} from "lucide-react";
import CollapsiblePanel from "./CollapsiblePanel";
import { ComplianceDisclaimer, AIBadge } from "./compliance/ComplianceDisclaimer";
import CreditBadge from "@/components/CreditBadge";  // iter1118

const VERDICT_COLORS = {
  excellent: { bg: "bg-green-500", text: "text-green-600", label_bg: "Отличен", label_en: "Excellent" },
  good: { bg: "bg-emerald-500", text: "text-emerald-600", label_bg: "Добър", label_en: "Good" },
  acceptable: { bg: "bg-amber-500", text: "text-amber-600", label_bg: "Приемлив", label_en: "Acceptable" },
  needs_changes: { bg: "bg-orange-500", text: "text-orange-600", label_bg: "Нужда от промени", label_en: "Needs changes" },
  poor: { bg: "bg-red-500", text: "text-red-600", label_bg: "Слаб", label_en: "Poor" },
};

const SEVERITY = {
  high: { color: "text-red-500", bg: "bg-red-50", dark: "bg-red-900/20", icon: AlertCircle, label_bg: "Високо", label_en: "High" },
  medium: { color: "text-amber-500", bg: "bg-amber-50", dark: "bg-amber-900/20", icon: AlertTriangle, label_bg: "Средно", label_en: "Medium" },
  low: { color: "text-blue-500", bg: "bg-blue-50", dark: "bg-blue-900/20", icon: Info, label_bg: "Ниско", label_en: "Low" },
};

export default function MealPlanReview() {
  const { API, user, refreshUser } = useAuth();
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const bg = language === "bg";

  const [planText, setPlanText] = useState("");
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [reviewId, setReviewId] = useState(null);
  const [copying, setCopying] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const fileInputRef = useRef(null);

  const loadHistory = useCallback(async () => {
    setLoadingHistory(true);
    try {
      const { data } = await axios.get(`${API}/meal-review/history`, { params: { limit: 20 } });
      setHistory(data.items || []);
    } catch {
      setHistory([]);
    }
    setLoadingHistory(false);
  }, [API]);

  useEffect(() => {
    if (showHistory && history.length === 0) loadHistory();
  }, [showHistory, history.length, loadHistory]);

  const openSavedReview = (item) => {
    setResult({
      overall_score: item.overall_score,
      verdict: item.verdict,
      summary: item.summary || "",
      good_points: item.good_points || [],
      issues: item.issues || [],
      parsed_meals: item.parsed_meals || [],
      profile_adjustments: item.profile_adjustments || "",
    });
    setReviewId(item.id);
    setShowHistory(false);
  };

  const credits = user?.credits ?? 0;
  const canAnalyze = credits >= 5 && (planText.trim().length > 20 || file);

  const handleFileSelect = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const isImg = f.type.startsWith("image/");
    const isPdf = f.type === "application/pdf" || f.name.toLowerCase().endsWith(".pdf");
    if (!isImg && !isPdf) {
      toast.error(bg ? "Само изображение или PDF" : "Image or PDF only");
      return;
    }
    const maxMB = isImg ? 10 : 15;
    if (f.size > maxMB * 1024 * 1024) {
      toast.error(bg ? `Файлът е твърде голям (макс ${maxMB} MB)` : `File too large (max ${maxMB} MB)`);
      return;
    }
    setFile(f);
  };

  const analyze = async () => {
    if (!canAnalyze) return;
    setLoading(true);
    setResult(null);
    try {
      const form = new FormData();
      if (planText.trim()) form.append("text", planText.trim());
      if (file) form.append("file", file);
      form.append("lang", bg ? "bg" : "en");
      const { data } = await axios.post(`${API}/meal-review/analyze`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data.result);
      setReviewId(data.id);
      // Refresh history to include the new review
      setHistory([]);
      await refreshUser?.();
      toast.success(bg ? "Анализът е готов" : "Analysis ready");
    } catch (e) {
      const detail = e.response?.data?.detail || "";
      if (e.response?.status === 402) {
        toast.error(bg ? "Достигнахте лимита си — надградете за повече анализи" : "You've reached your limit — upgrade for more analyses");
      } else {
        toast.error(detail || (bg ? "Грешка при анализ" : "Analysis error"));
      }
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setResult(null);
    setReviewId(null);
    setPlanText("");
    setFile(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const copyToMealLog = async () => {
    if (!reviewId) return;
    setCopying(true);
    try {
      const { data } = await axios.post(`${API}/meal-review/copy-to-log/${reviewId}`);
      toast.success(bg
        ? `Добавени ${data.added_count} храни в Хранителния дневник`
        : `Added ${data.added_count} foods to Meal Log`);
      window.dispatchEvent(new CustomEvent("meallog:refresh"));
    } catch (e) {
      toast.error(e.response?.data?.detail || (bg ? "Грешка при копиране" : "Copy error"));
    } finally {
      setCopying(false);
    }
  };

  const verdictKey = result?.verdict || "acceptable";
  const verdict = VERDICT_COLORS[verdictKey] || VERDICT_COLORS.acceptable;
  const score = Math.max(0, Math.min(100, Number(result?.overall_score) || 0));

  return (
    <CollapsiblePanel
      title={bg ? "Преглед на хранителен режим" : "Meal Plan Review"}
      subtitle={bg ? "Качете режима — получавате клинична оценка" : "Upload plan — get clinical review"}
      icon={ClipboardCheck}
      iconColor="#EC4899"
      credits={5}
      defaultOpen={false}
      storageKey="meal-plan-review"
      testId="meal-plan-review"
    >
      {!result ? (
        <div className="space-y-3">
          <div className="flex items-center gap-2"><AIBadge /></div>
          <ComplianceDisclaimer context="meal_plan" />
          <Textarea
            value={planText}
            onChange={(e) => setPlanText(e.target.value)}
            placeholder={bg
              ? "Пример:\nЗакуска: овесени ядки 60g + мляко 200ml + банан\nОбяд: пилешки гърди 150g, кафяв ориз 100g, броколи 150g\nВечеря: риба на фурна 180g, зеленчукова салата\n..."
              : "Example:\nBreakfast: oats 60g + milk 200ml + banana\nLunch: chicken breast 150g, brown rice 100g, broccoli 150g\n..."}
            rows={6}
            className={`text-xs font-mono ${isDark ? "bg-[hsl(223,47%,10%)] border-[hsl(223,47%,20%)] text-gray-200" : "bg-gray-50 border-gray-200"}`}
            data-testid="meal-plan-text"
          />

          <div className="flex items-center justify-between gap-2">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*,application/pdf"
              className="hidden"
              onChange={handleFileSelect}
              data-testid="meal-plan-file-input"
            />
            {file ? (
              <div className={`flex items-center gap-2 px-2.5 py-1.5 rounded-lg flex-1 min-w-0 text-xs ${isDark ? "bg-[hsl(223,47%,12%)]" : "bg-gray-100"}`} data-testid="meal-plan-file-chip">
                {file.type.startsWith("image/") ? <ImageIcon className="w-3.5 h-3.5 text-[#EC4899]" /> : <FileText className="w-3.5 h-3.5 text-[#EC4899]" />}
                <span className={`truncate ${isDark ? "text-gray-300" : "text-gray-700"}`}>{file.name}</span>
                <span className={isDark ? "text-gray-500" : "text-gray-400"}>
                  {(file.size / 1024 / 1024).toFixed(1)} MB
                </span>
                <button onClick={() => { setFile(null); if (fileInputRef.current) fileInputRef.current.value = ""; }} className="flex-shrink-0" data-testid="meal-plan-file-remove">
                  <X className="w-3.5 h-3.5 text-gray-400" />
                </button>
              </div>
            ) : (
              <Button
                variant="outline"
                size="sm"
                onClick={() => fileInputRef.current?.click()}
                className={`h-9 text-xs ${isDark ? "bg-[hsl(223,47%,10%)] border-[hsl(223,47%,20%)] text-gray-300" : ""}`}
                data-testid="meal-plan-attach-btn"
              >
                <Upload className="w-3.5 h-3.5 mr-1.5" />
                {bg ? "Прикачи PDF/снимка" : "Attach PDF/image"}
              </Button>
            )}
            <Button
              onClick={analyze}
              disabled={!canAnalyze || loading}
              className="h-9 bg-gradient-to-r from-[#EC4899] to-[#8B5CF6] hover:opacity-90 text-white text-xs font-semibold disabled:opacity-50"
              data-testid="meal-plan-analyze-btn"
              title={bg ? "Прегледай хранителен режим (1 кредит)" : "Review meal plan (1 credit)"}
            >
              {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Sparkles className="w-3.5 h-3.5 mr-1.5" />}
              {loading ? (bg ? "Анализ..." : "Analyzing...") : (bg ? "Анализирай" : "Analyze")}
              <CreditBadge featureId="meal_plan_review" fallbackCost={1} variant="inline" showLabel={false} className="ml-1" />
            </Button>
          </div>

          {/* History toggle */}
          <button
            onClick={() => setShowHistory(v => !v)}
            className={`w-full flex items-center justify-between px-2.5 py-1.5 rounded-lg text-xs font-medium ${
              isDark ? "bg-[hsl(223,47%,10%)] hover:bg-[hsl(223,47%,14%)] text-gray-300" : "bg-gray-50 hover:bg-gray-100 text-gray-700"
            }`}
            data-testid="meal-plan-history-toggle"
          >
            <span className="flex items-center gap-1.5">
              <HistoryIcon className="w-3.5 h-3.5 text-[#EC4899]" />
              {showHistory ? (bg ? "Скрий минали прегледи" : "Hide past reviews") : (bg ? "Виж минали прегледи" : "View past reviews")}
            </span>
            <ChevronRight className={`w-3.5 h-3.5 transition-transform ${showHistory ? "rotate-90" : ""}`} />
          </button>

          {showHistory && (
            <div className={`space-y-1.5 p-2 rounded-lg max-h-64 overflow-y-auto ${isDark ? "bg-[hsl(223,47%,8%)]" : "bg-gray-50"}`} data-testid="meal-plan-history-list">
              {loadingHistory && <Loader2 className="w-4 h-4 animate-spin mx-auto" />}
              {!loadingHistory && history.length === 0 && (
                <p className={`text-[11px] italic text-center py-2 ${isDark ? "text-gray-500" : "text-gray-400"}`}>
                  {bg ? "Нямате запазени прегледи" : "No saved reviews yet"}
                </p>
              )}
              {!loadingHistory && history.map((item) => {
                const v = VERDICT_COLORS[item.verdict] || VERDICT_COLORS.acceptable;
                const d = new Date(item.created_at).toLocaleDateString(language === "bg" ? "bg-BG" : "en-US", {
                  day: "numeric", month: "short", hour: "2-digit", minute: "2-digit",
                });
                return (
                  <button
                    key={item.id}
                    onClick={() => openSavedReview(item)}
                    className={`w-full p-2 rounded-lg text-left flex items-center gap-2 ${
                      isDark ? "bg-[hsl(222,47%,11%)] hover:bg-[hsl(223,47%,14%)]" : "bg-white hover:bg-gray-50 shadow-sm"
                    }`}
                    data-testid={`meal-plan-history-item-${item.id}`}
                  >
                    <div className={`w-8 h-8 rounded-lg ${v.bg} flex items-center justify-center flex-shrink-0`}>
                      <span className="text-[10px] font-bold text-white">{item.overall_score}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`text-[11px] font-semibold ${v.text}`}>
                        {bg ? v.label_bg : v.label_en}
                      </p>
                      <p className={`text-[9px] ${isDark ? "text-gray-500" : "text-gray-400"}`}>
                        {d} · {(item.parsed_meals || []).length} {bg ? "ястия" : "meals"}
                      </p>
                    </div>
                    <ChevronRight className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                  </button>
                );
              })}
            </div>
          )}

          {credits < 5 && (
            <p className="text-[11px] text-amber-500" data-testid="meal-plan-low-credits">
              {bg ? "Достигнахте лимита на пробния / плана. Надградете за повече анализи." : "You've reached your trial / plan limit. Upgrade for more analyses."}
            </p>
          )}
        </div>
      ) : (
        <div className="space-y-4" data-testid="meal-plan-result">
          {/* Score + Verdict */}
          <div className={`flex items-center gap-4 p-3 rounded-xl ${isDark ? "bg-[hsl(223,47%,10%)]" : "bg-gradient-to-r from-pink-50 to-purple-50"}`}>
            <div className="relative w-20 h-20 flex-shrink-0">
              <svg viewBox="0 0 36 36" className="w-full h-full -rotate-90">
                <circle cx="18" cy="18" r="15" fill="none" strokeWidth="3" className={isDark ? "stroke-gray-800" : "stroke-gray-200"} />
                <circle cx="18" cy="18" r="15" fill="none" strokeWidth="3" strokeLinecap="round"
                  strokeDasharray={`${(score / 100) * 94.2} 94.2`}
                  className={`${verdict.text} transition-all duration-700`} stroke="currentColor" />
              </svg>
              <div className={`absolute inset-0 flex flex-col items-center justify-center ${verdict.text}`}>
                <span className="text-xl font-bold">{score}</span>
                <span className="text-[9px]">/100</span>
              </div>
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full text-white ${verdict.bg}`}>
                  {bg ? verdict.label_bg : verdict.label_en}
                </span>
              </div>
              <p className={`text-xs ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                {result.summary}
              </p>
            </div>
          </div>

          {/* Macros estimate */}
          {result.macro_estimate && (
            <div className={`grid grid-cols-6 gap-1.5 p-2 rounded-lg ${isDark ? "bg-[hsl(223,47%,10%)]" : "bg-gray-50"}`} data-testid="meal-plan-macros">
              {[
                { k: "calories", label_bg: "kcal", label_en: "kcal", color: "#F59E0B" },
                { k: "protein_g", label_bg: "P", label_en: "P", color: "#0EA5E9" },
                { k: "carbs_g", label_bg: "C", label_en: "C", color: "#10B981" },
                { k: "fat_g", label_bg: "F", label_en: "F", color: "#8B5CF6" },
                { k: "fiber_g", label_bg: "Fi", label_en: "Fi", color: "#6366F1" },
                { k: "dil_estimate", label_bg: "DIL", label_en: "DIL", color: "#EF4444" },
              ].map(m => (
                <div key={m.k} className="text-center">
                  <div className="text-sm font-bold" style={{ color: m.color }}>{Math.round(result.macro_estimate[m.k] || 0)}</div>
                  <div className={`text-[9px] ${isDark ? "text-gray-500" : "text-gray-400"}`}>{bg ? m.label_bg : m.label_en}</div>
                </div>
              ))}
            </div>
          )}

          {/* Good points */}
          {result.good_points?.length > 0 && (
            <div data-testid="meal-plan-good-points">
              <h4 className={`text-xs font-bold mb-1.5 flex items-center gap-1.5 ${isDark ? "text-green-400" : "text-green-600"}`}>
                <CheckCircle2 className="w-3.5 h-3.5" />
                {bg ? "Силни страни" : "Strong points"}
              </h4>
              <ul className="space-y-1 pl-1">
                {result.good_points.slice(0, 6).map((p, i) => (
                  <li key={`gp-${i}-${String(p).slice(0, 18)}`} className={`text-[11px] pl-4 relative ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                    <span className="absolute left-0 text-green-500">✓</span> {p}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Issues */}
          {result.issues?.length > 0 && (
            <div data-testid="meal-plan-issues">
              <h4 className={`text-xs font-bold mb-1.5 flex items-center gap-1.5 ${isDark ? "text-amber-400" : "text-amber-600"}`}>
                <AlertTriangle className="w-3.5 h-3.5" />
                {bg ? "Проблеми" : "Issues"}
              </h4>
              <div className="space-y-2">
                {result.issues.map((iss, i) => {
                  const sev = SEVERITY[iss.severity] || SEVERITY.low;
                  const Icon = sev.icon;
                  return (
                    <div key={`iss-${i}-${String(iss.title || '').slice(0, 18)}`} className={`p-2.5 rounded-lg ${isDark ? sev.dark : sev.bg}`}>
                      <div className="flex items-start gap-2">
                        <Icon className={`w-3.5 h-3.5 mt-0.5 flex-shrink-0 ${sev.color}`} />
                        <div className="flex-1 min-w-0">
                          <p className={`text-xs font-semibold ${isDark ? "text-white" : "text-gray-800"}`}>
                            {iss.title}
                            <span className={`ml-1.5 text-[9px] font-medium px-1.5 py-0.5 rounded ${sev.color}`}>
                              {bg ? sev.label_bg : sev.label_en}
                            </span>
                          </p>
                          {iss.why && <p className={`text-[11px] mt-0.5 ${isDark ? "text-gray-400" : "text-gray-600"}`}>{iss.why}</p>}
                          {iss.fix && <p className={`text-[11px] mt-1 font-medium ${isDark ? "text-emerald-400" : "text-emerald-600"}`}>→ {iss.fix}</p>}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Substitutions */}
          {result.substitutions?.length > 0 && (
            <div data-testid="meal-plan-subs">
              <h4 className={`text-xs font-bold mb-1.5 flex items-center gap-1.5 ${isDark ? "text-purple-400" : "text-purple-600"}`}>
                <ListChecks className="w-3.5 h-3.5" />
                {bg ? "Предложени замени" : "Suggested swaps"}
              </h4>
              <div className="space-y-1.5">
                {result.substitutions.slice(0, 8).map((s, i) => (
                  <div key={`sub-${i}-${String(s.replace || '').slice(0, 15)}`} className={`p-2 rounded-lg text-[11px] ${isDark ? "bg-[hsl(223,47%,12%)]" : "bg-white border border-gray-100"}`}>
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className={`line-through ${isDark ? "text-gray-500" : "text-gray-400"}`}>{s.replace}</span>
                      <span className="text-gray-400">→</span>
                      <span className={`font-medium ${isDark ? "text-emerald-400" : "text-emerald-600"}`}>{s.with}</span>
                    </div>
                    {s.reason && <p className={`text-[10px] mt-0.5 ${isDark ? "text-gray-500" : "text-gray-500"}`}>{s.reason}</p>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Considerations */}
          {(result.genetics_considerations?.length > 0 || result.condition_considerations?.length > 0) && (
            <div className={`p-2.5 rounded-lg text-[11px] space-y-1.5 ${isDark ? "bg-[#0EA5E9]/10" : "bg-sky-50"}`}>
              {result.genetics_considerations?.length > 0 && (
                <div>
                  <p className={`font-semibold mb-0.5 ${isDark ? "text-[#0EA5E9]" : "text-sky-700"}`}>
                    {bg ? "🧬 Нутригеномика" : "🧬 Nutrigenomics"}
                  </p>
                  <ul className="space-y-0.5 pl-4">
                    {result.genetics_considerations.slice(0, 4).map((c, i) => (
                      <li key={`g-${i}`} className={`list-disc ${isDark ? "text-gray-400" : "text-gray-600"}`}>{c}</li>
                    ))}
                  </ul>
                </div>
              )}
              {result.condition_considerations?.length > 0 && (
                <div>
                  <p className={`font-semibold mb-0.5 ${isDark ? "text-[#0EA5E9]" : "text-sky-700"}`}>
                    {bg ? "🩺 Вашите състояния" : "🩺 Your conditions"}
                  </p>
                  <ul className="space-y-0.5 pl-4">
                    {result.condition_considerations.slice(0, 4).map((c, i) => (
                      <li key={`c-${i}`} className={`list-disc ${isDark ? "text-gray-400" : "text-gray-600"}`}>{c}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {/* Meal timing */}
          {result.meal_timing_notes && (
            <p className={`text-[11px] italic ${isDark ? "text-gray-400" : "text-gray-500"}`}>
              {bg ? "⏰ Времево разпределение: " : "⏰ Meal timing: "}{result.meal_timing_notes}
            </p>
          )}

          <div className="flex items-center gap-2">
            {result.parsed_meals?.length > 0 && (
              <Button
                onClick={copyToMealLog}
                disabled={copying}
                className="flex-1 h-9 bg-gradient-to-r from-[#10B981] to-[#059669] hover:opacity-90 text-white text-xs font-semibold"
                data-testid="meal-plan-copy-to-log-btn"
              >
                {copying ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Copy className="w-3.5 h-3.5 mr-1.5" />}
                {bg ? "Копирай в Дневник" : "Copy to Meal Log"}
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={reset} className="h-9 text-xs" data-testid="meal-plan-new-btn">
              {bg ? "Нов анализ" : "New analysis"}
            </Button>
          </div>
        </div>
      )}
    </CollapsiblePanel>
  );
}
